import type {
  Customer,
  Transaction,
  InternalAccount,
  ExternalAccount,
  ApiToken,
  DashboardStats,
} from "../types";

// -- Currencies --
const USD = { code: "USD", name: "US Dollar", symbol: "$", decimals: 2 };
const BTC = { code: "BTC", name: "Bitcoin", symbol: "₿", decimals: 8 };
const USDC = { code: "USDC", name: "USD Coin", symbol: "$", decimals: 2 };
const EUR = { code: "EUR", name: "Euro", symbol: "€", decimals: 2 };
const BRL = { code: "BRL", name: "Brazilian Real", symbol: "R$", decimals: 2 };
const MXN = {
  code: "MXN",
  name: "Mexican Peso",
  symbol: "MX$",
  decimals: 2,
};
const GBP = {
  code: "GBP",
  name: "British Pound",
  symbol: "£",
  decimals: 2,
};
const NGN = {
  code: "NGN",
  name: "Nigerian Naira",
  symbol: "₦",
  decimals: 2,
};
const INR = {
  code: "INR",
  name: "Indian Rupee",
  symbol: "₹",
  decimals: 2,
};

export const CURRENCIES: Record<string, typeof USD> = { USD, BTC, USDC, EUR, BRL, MXN, GBP, NGN, INR };

// -- Customers --
export const mockCustomers: Customer[] = [
  {
    id: "cust_01HQ3X5K7N2M8P",
    platformCustomerId: "user-001",
    customerType: "INDIVIDUAL",
    fullName: "Alice Johnson",
    email: "alice@example.com",
    umaAddress: "$alice@partner.grid.com",
    kycStatus: "APPROVED",
    address: {
      line1: "123 Main St",
      city: "San Francisco",
      state: "CA",
      postalCode: "94102",
      country: "US",
    },
    createdAt: "2025-08-15T10:30:00Z",
    updatedAt: "2025-08-16T14:20:00Z",
  },
  {
    id: "cust_01HQ3X5K7N2M8Q",
    platformCustomerId: "user-002",
    customerType: "INDIVIDUAL",
    fullName: "Bob Martinez",
    email: "bob@example.com",
    umaAddress: "$bob@partner.grid.com",
    kycStatus: "PENDING_REVIEW",
    address: {
      line1: "456 Oak Ave",
      city: "Miami",
      state: "FL",
      postalCode: "33101",
      country: "US",
    },
    createdAt: "2025-09-01T08:15:00Z",
    updatedAt: "2025-09-01T08:15:00Z",
  },
  {
    id: "cust_01HQ3X5K7N2M8R",
    platformCustomerId: "user-003",
    customerType: "BUSINESS",
    fullName: "TechCorp International",
    email: "finance@techcorp.com",
    umaAddress: "$techcorp@partner.grid.com",
    kycStatus: "APPROVED",
    address: {
      line1: "789 Innovation Blvd",
      city: "Austin",
      state: "TX",
      postalCode: "73301",
      country: "US",
    },
    createdAt: "2025-07-20T12:00:00Z",
    updatedAt: "2025-10-05T09:30:00Z",
  },
  {
    id: "cust_01HQ3X5K7N2M8S",
    platformCustomerId: "user-004",
    customerType: "INDIVIDUAL",
    fullName: "Maria Silva",
    email: "maria@example.com.br",
    umaAddress: "$maria@partner.grid.com",
    kycStatus: "REJECTED",
    address: {
      line1: "Rua das Flores, 100",
      city: "São Paulo",
      state: "SP",
      postalCode: "01001-000",
      country: "BR",
    },
    createdAt: "2025-09-10T16:45:00Z",
    updatedAt: "2025-09-12T11:20:00Z",
  },
  {
    id: "cust_01HQ3X5K7N2M8T",
    platformCustomerId: "user-005",
    customerType: "INDIVIDUAL",
    fullName: "James Chen",
    email: "james.chen@example.com",
    umaAddress: "$james@partner.grid.com",
    kycStatus: "MANUALLY_APPROVED",
    address: {
      line1: "88 Market St",
      city: "New York",
      state: "NY",
      postalCode: "10001",
      country: "US",
    },
    createdAt: "2025-08-01T09:00:00Z",
    updatedAt: "2025-08-03T15:10:00Z",
  },
  {
    id: "cust_01HQ3X5K7N2M8U",
    platformCustomerId: "user-006",
    customerType: "BUSINESS",
    fullName: "Nordic Payments AB",
    email: "ops@nordicpay.se",
    umaAddress: "$nordic@partner.grid.com",
    kycStatus: "APPROVED",
    address: {
      line1: "Kungsgatan 12",
      city: "Stockholm",
      state: "",
      postalCode: "111 43",
      country: "SE",
    },
    createdAt: "2025-06-15T07:30:00Z",
    updatedAt: "2025-10-01T10:00:00Z",
  },
  {
    id: "cust_01HQ3X5K7N2M8V",
    platformCustomerId: "user-007",
    customerType: "INDIVIDUAL",
    fullName: "Priya Patel",
    email: "priya@example.in",
    umaAddress: "$priya@partner.grid.com",
    kycStatus: "EXPIRED",
    address: {
      line1: "42 MG Road",
      city: "Mumbai",
      state: "MH",
      postalCode: "400001",
      country: "IN",
    },
    createdAt: "2025-05-20T13:00:00Z",
    updatedAt: "2025-10-20T13:00:00Z",
  },
  {
    id: "cust_01HQ3X5K7N2M8W",
    platformCustomerId: "user-008",
    customerType: "INDIVIDUAL",
    fullName: "Carlos Hernandez",
    email: "carlos@example.mx",
    umaAddress: "$carlos@partner.grid.com",
    kycStatus: "PENDING_REVIEW",
    address: {
      line1: "Av. Reforma 222",
      city: "Mexico City",
      state: "CDMX",
      postalCode: "06600",
      country: "MX",
    },
    createdAt: "2025-10-05T11:30:00Z",
    updatedAt: "2025-10-05T11:30:00Z",
  },
  {
    id: "cust_01HQ3X5K7N2M8X",
    platformCustomerId: "user-009",
    customerType: "BUSINESS",
    fullName: "FinBridge GmbH",
    email: "hello@finbridge.de",
    umaAddress: "$finbridge@partner.grid.com",
    kycStatus: "CANCELED",
    address: {
      line1: "Friedrichstraße 100",
      city: "Berlin",
      state: "",
      postalCode: "10117",
      country: "DE",
    },
    createdAt: "2025-09-25T08:00:00Z",
    updatedAt: "2025-10-15T16:00:00Z",
  },
  {
    id: "cust_01HQ3X5K7N2M8Y",
    platformCustomerId: "user-010",
    customerType: "INDIVIDUAL",
    fullName: "Emily Watson",
    email: "emily.w@example.co.uk",
    umaAddress: "$emily@partner.grid.com",
    kycStatus: "APPROVED",
    address: {
      line1: "14 King's Road",
      city: "London",
      state: "",
      postalCode: "SW3 4TW",
      country: "GB",
    },
    createdAt: "2025-07-10T10:20:00Z",
    updatedAt: "2025-07-12T09:45:00Z",
  },
  {
    id: "cust_01HQ3X5K7N2M8Z",
    platformCustomerId: "user-011",
    customerType: "INDIVIDUAL",
    fullName: "Kenji Tanaka",
    email: "kenji@example.jp",
    umaAddress: "$kenji@partner.grid.com",
    kycStatus: "MANUALLY_REJECTED",
    address: {
      line1: "1-2-3 Shibuya",
      city: "Tokyo",
      state: "",
      postalCode: "150-0002",
      country: "JP",
    },
    createdAt: "2025-10-01T06:00:00Z",
    updatedAt: "2025-10-08T14:30:00Z",
  },
  {
    id: "cust_01HQ3X5K7N2M9A",
    platformCustomerId: "user-012",
    customerType: "BUSINESS",
    fullName: "PayLatam SA",
    email: "admin@paylatam.co",
    umaAddress: "$paylatam@partner.grid.com",
    kycStatus: "APPROVED",
    address: {
      line1: "Calle 100 #15-20",
      city: "Bogotá",
      state: "",
      postalCode: "110111",
      country: "CO",
    },
    createdAt: "2025-08-22T14:00:00Z",
    updatedAt: "2025-08-25T10:00:00Z",
  },
];

// -- Internal Accounts (USD, USDC, BTC for each customer) --
// Internal accounts are auto-created per currency when a customer is onboarded.
// In the API: GET /customers/internal-accounts?customerId=<id>
function generateCustomerInternalAccounts(): InternalAccount[] {
  const accounts: InternalAccount[] = [];
  const balances: Record<string, { usd: number; usdc: number; btc: number }> = {
    "cust_01HQ3X5K7N2M8P": { usd: 1523400, usdc: 5430000, btc: 15000000 },
    "cust_01HQ3X5K7N2M8Q": { usd: 250000, usdc: 100000, btc: 500000 },
    "cust_01HQ3X5K7N2M8R": { usd: 98750000, usdc: 250000000, btc: 75000000 },
    "cust_01HQ3X5K7N2M8S": { usd: 45000, usdc: 0, btc: 0 },
    "cust_01HQ3X5K7N2M8T": { usd: 875000, usdc: 320000, btc: 8500000 },
    "cust_01HQ3X5K7N2M8U": { usd: 4520000, usdc: 12000000, btc: 100000000 },
    "cust_01HQ3X5K7N2M8V": { usd: 150000, usdc: 50000, btc: 2000000 },
    "cust_01HQ3X5K7N2M8W": { usd: 320000, usdc: 0, btc: 1000000 },
    "cust_01HQ3X5K7N2M8X": { usd: 6200000, usdc: 8500000, btc: 45000000 },
    "cust_01HQ3X5K7N2M8Y": { usd: 1200000, usdc: 500000, btc: 3000000 },
    "cust_01HQ3X5K7N2M8Z": { usd: 80000, usdc: 25000, btc: 500000 },
    "cust_01HQ3X5K7N2M9A": { usd: 15800000, usdc: 22000000, btc: 60000000 },
  };

  let idx = 1;
  for (const cust of mockCustomers) {
    const bal = balances[cust.id] || { usd: 0, usdc: 0, btc: 0 };
    accounts.push({
      id: `ia_${String(idx++).padStart(3, "0")}`,
      customerId: cust.id,
      balance: { amount: bal.usd, currency: USD },
      createdAt: cust.createdAt,
      updatedAt: "2025-10-20T09:00:00Z",
    });
    accounts.push({
      id: `ia_${String(idx++).padStart(3, "0")}`,
      customerId: cust.id,
      balance: { amount: bal.usdc, currency: USDC },
      createdAt: cust.createdAt,
      updatedAt: "2025-10-20T09:00:00Z",
    });
    accounts.push({
      id: `ia_${String(idx++).padStart(3, "0")}`,
      customerId: cust.id,
      balance: { amount: bal.btc, currency: BTC },
      createdAt: cust.createdAt,
      updatedAt: "2025-10-20T09:00:00Z",
    });
  }
  return accounts;
}

export const mockInternalAccounts: InternalAccount[] = generateCustomerInternalAccounts();

// -- Platform / Corporate Internal Accounts (Lightspark Partner Treasury) --
// In the API: GET /platform/internal-accounts
export const mockPlatformInternalAccounts: InternalAccount[] = [
  {
    id: "ia_platform_usd",
    customerId: "PLATFORM",
    balance: { amount: 500000000, currency: USD },
    createdAt: "2025-01-01T00:00:00Z",
    updatedAt: "2025-10-20T12:00:00Z",
  },
  {
    id: "ia_platform_eur",
    customerId: "PLATFORM",
    balance: { amount: 250000000, currency: EUR },
    createdAt: "2025-01-01T00:00:00Z",
    updatedAt: "2025-10-20T12:00:00Z",
  },
  {
    id: "ia_platform_usdc",
    customerId: "PLATFORM",
    balance: { amount: 750000000, currency: USDC },
    createdAt: "2025-01-01T00:00:00Z",
    updatedAt: "2025-10-20T12:00:00Z",
  },
  {
    id: "ia_platform_btc",
    customerId: "PLATFORM",
    balance: { amount: 500000000, currency: BTC },
    createdAt: "2025-01-01T00:00:00Z",
    updatedAt: "2025-10-20T12:00:00Z",
  },
  {
    id: "ia_platform_gbp",
    customerId: "PLATFORM",
    balance: { amount: 180000000, currency: GBP },
    createdAt: "2025-01-01T00:00:00Z",
    updatedAt: "2025-10-20T12:00:00Z",
  },
];

// -- Platform / Corporate External Accounts --
// In the API: GET /platform/external-accounts, POST /platform/external-accounts
export const mockPlatformExternalAccounts: ExternalAccount[] = [
  {
    id: "ea_platform_01",
    customerId: "PLATFORM",
    status: "ACTIVE",
    currency: USD,
    accountType: "US_ACCOUNT",
    accountLabel: "JPMorgan Corporate ****9001",
    defaultUmaDepositAccount: true,
    createdAt: "2025-01-01T00:00:00Z",
  },
  {
    id: "ea_platform_02",
    customerId: "PLATFORM",
    status: "ACTIVE",
    currency: EUR,
    accountType: "IBAN",
    accountLabel: "IBAN: DE89 3704 0044 ****",
    defaultUmaDepositAccount: false,
    createdAt: "2025-01-01T00:00:00Z",
  },
  {
    id: "ea_platform_03",
    customerId: "PLATFORM",
    status: "ACTIVE",
    currency: GBP,
    accountType: "GBP_ACCOUNT",
    accountLabel: "Barclays: ****7890 (20-45-67)",
    defaultUmaDepositAccount: false,
    createdAt: "2025-01-15T00:00:00Z",
  },
  {
    id: "ea_platform_04",
    customerId: "PLATFORM",
    status: "ACTIVE",
    currency: USDC,
    accountType: "SOLANA_WALLET",
    accountLabel: "Solana: 8xLA...m4Rp",
    defaultUmaDepositAccount: false,
    createdAt: "2025-02-01T00:00:00Z",
  },
  {
    id: "ea_platform_05",
    customerId: "PLATFORM",
    status: "ACTIVE",
    currency: BTC,
    accountType: "SPARK_WALLET",
    accountLabel: "Spark: spark1...treasury",
    defaultUmaDepositAccount: false,
    createdAt: "2025-02-01T00:00:00Z",
  },
  {
    id: "ea_platform_06",
    customerId: "PLATFORM",
    status: "ACTIVE",
    currency: USDC,
    accountType: "BASE_WALLET",
    accountLabel: "Base: 0x4f7a...c2e1",
    defaultUmaDepositAccount: false,
    createdAt: "2025-03-01T00:00:00Z",
  },
];

// -- External Accounts --
// In the API: GET /customers/external-accounts?customerId=<id>
// POST /customers/external-accounts to create new ones
export const mockExternalAccounts: ExternalAccount[] = [
  // Alice Johnson - US customer
  {
    id: "ea_01",
    customerId: "cust_01HQ3X5K7N2M8P",
    status: "ACTIVE",
    currency: USD,
    accountType: "US_ACCOUNT",
    accountLabel: "Chase Checking ****4521",
    defaultUmaDepositAccount: true,
    createdAt: "2025-08-15T10:30:00Z",
  },
  {
    id: "ea_02",
    customerId: "cust_01HQ3X5K7N2M8P",
    status: "ACTIVE",
    currency: BTC,
    accountType: "SPARK_WALLET",
    accountLabel: "Spark: spark1...alice",
    defaultUmaDepositAccount: false,
    createdAt: "2025-08-16T09:00:00Z",
  },
  {
    id: "ea_11",
    customerId: "cust_01HQ3X5K7N2M8P",
    status: "ACTIVE",
    currency: USDC,
    accountType: "SOLANA_WALLET",
    accountLabel: "Solana: 7xKX...m3Qp",
    defaultUmaDepositAccount: false,
    createdAt: "2025-09-01T14:00:00Z",
  },
  // TechCorp - US business
  {
    id: "ea_03",
    customerId: "cust_01HQ3X5K7N2M8R",
    status: "ACTIVE",
    currency: USD,
    accountType: "US_ACCOUNT",
    accountLabel: "Business Acct ****8812",
    defaultUmaDepositAccount: true,
    createdAt: "2025-07-20T12:00:00Z",
  },
  {
    id: "ea_04",
    customerId: "cust_01HQ3X5K7N2M8R",
    status: "ACTIVE",
    currency: USDC,
    accountType: "BASE_WALLET",
    accountLabel: "Base: 0x1a2b...9f8e",
    defaultUmaDepositAccount: false,
    createdAt: "2025-07-21T08:00:00Z",
  },
  // Maria Silva - Brazil
  {
    id: "ea_05",
    customerId: "cust_01HQ3X5K7N2M8S",
    status: "PENDING_VERIFICATION",
    currency: BRL,
    accountType: "PIX",
    accountLabel: "PIX: maria@example.com.br",
    defaultUmaDepositAccount: true,
    createdAt: "2025-09-10T16:45:00Z",
  },
  // James Chen - US
  {
    id: "ea_06",
    customerId: "cust_01HQ3X5K7N2M8T",
    status: "ACTIVE",
    currency: USD,
    accountType: "US_ACCOUNT",
    accountLabel: "BofA Savings ****7733",
    defaultUmaDepositAccount: true,
    createdAt: "2025-08-01T09:00:00Z",
  },
  // Nordic Payments - Sweden (SEPA/IBAN)
  {
    id: "ea_07",
    customerId: "cust_01HQ3X5K7N2M8U",
    status: "ACTIVE",
    currency: EUR,
    accountType: "IBAN",
    accountLabel: "IBAN: SE35 5000 0000 ****",
    defaultUmaDepositAccount: true,
    createdAt: "2025-06-15T07:30:00Z",
  },
  // Priya Patel - India (UPI)
  {
    id: "ea_12",
    customerId: "cust_01HQ3X5K7N2M8V",
    status: "ACTIVE",
    currency: INR,
    accountType: "UPI",
    accountLabel: "UPI: priya@okaxis",
    defaultUmaDepositAccount: true,
    createdAt: "2025-05-20T13:00:00Z",
  },
  // Carlos Hernandez - Mexico (CLABE)
  {
    id: "ea_08",
    customerId: "cust_01HQ3X5K7N2M8W",
    status: "PENDING_VERIFICATION",
    currency: MXN,
    accountType: "CLABE",
    accountLabel: "CLABE: 0021 8001 ****",
    defaultUmaDepositAccount: true,
    createdAt: "2025-10-05T11:30:00Z",
  },
  // Emily Watson - UK (GBP_ACCOUNT)
  {
    id: "ea_09",
    customerId: "cust_01HQ3X5K7N2M8Y",
    status: "ACTIVE",
    currency: GBP,
    accountType: "GBP_ACCOUNT",
    accountLabel: "Sort: 20-45-67, Acct: ****3456",
    defaultUmaDepositAccount: true,
    createdAt: "2025-07-10T10:20:00Z",
  },
  // PayLatam - Colombia (US_ACCOUNT for USD payout)
  {
    id: "ea_10",
    customerId: "cust_01HQ3X5K7N2M9A",
    status: "ACTIVE",
    currency: USD,
    accountType: "US_ACCOUNT",
    accountLabel: "Citi Business ****2244",
    defaultUmaDepositAccount: true,
    createdAt: "2025-08-22T14:00:00Z",
  },
  {
    id: "ea_13",
    customerId: "cust_01HQ3X5K7N2M9A",
    status: "ACTIVE",
    currency: USDC,
    accountType: "POLYGON_WALLET",
    accountLabel: "Polygon: 0xabc...def0",
    defaultUmaDepositAccount: false,
    createdAt: "2025-08-23T10:00:00Z",
  },
];

// -- Transactions --
// In the API: GET /transactions, GET /transactions/:id
export const mockTransactions: Transaction[] = [
  {
    id: "tx_01HQ4A1B2C3D",
    customerId: "cust_01HQ3X5K7N2M8P",
    platformCustomerId: "user-001",
    type: "OUTGOING",
    status: "COMPLETED",
    sentAmount: { amount: 50000, currency: USD },
    receivedAmount: { amount: 48500, currency: EUR },
    exchangeRate: 0.97,
    fees: 150,
    description: "Payment to vendor",
    destination: "$vendor@uma.eu",
    source: "Internal USD Account",
    createdAt: "2025-10-18T14:30:00Z",
    settledAt: "2025-10-18T14:31:00Z",
  },
  {
    id: "tx_01HQ4A1B2C3E",
    customerId: "cust_01HQ3X5K7N2M8P",
    platformCustomerId: "user-001",
    type: "INCOMING",
    status: "COMPLETED",
    sentAmount: { amount: 10000000, currency: BTC },
    receivedAmount: { amount: 680000, currency: USD },
    exchangeRate: 68000,
    fees: 0,
    description: "BTC deposit conversion",
    destination: "Internal USD Account",
    source: "$sender@wallet.btc",
    createdAt: "2025-10-17T09:15:00Z",
    settledAt: "2025-10-17T09:16:00Z",
  },
  {
    id: "tx_01HQ4A1B2C3F",
    customerId: "cust_01HQ3X5K7N2M8P",
    platformCustomerId: "user-001",
    type: "OUTGOING",
    status: "PROCESSING",
    sentAmount: { amount: 250000, currency: USDC },
    receivedAmount: { amount: 1350000, currency: BRL },
    exchangeRate: 5.4,
    fees: 500,
    description: "Brazil supplier payment",
    destination: "PIX: supplier@corp.br",
    source: "Internal USDC Account",
    createdAt: "2025-10-20T08:00:00Z",
  },
  {
    id: "tx_01HQ4A1B2C3G",
    customerId: "cust_01HQ3X5K7N2M8R",
    platformCustomerId: "user-003",
    type: "OUTGOING",
    status: "COMPLETED",
    sentAmount: { amount: 10000000, currency: USD },
    receivedAmount: { amount: 9800000, currency: USDC },
    exchangeRate: 0.98,
    fees: 2000,
    description: "Treasury USDC conversion",
    destination: "Base: 0x1a2b...9f8e",
    source: "Internal USD Account",
    createdAt: "2025-10-19T16:00:00Z",
    settledAt: "2025-10-19T16:02:00Z",
  },
  {
    id: "tx_01HQ4A1B2C3H",
    customerId: "cust_01HQ3X5K7N2M8R",
    platformCustomerId: "user-003",
    type: "INCOMING",
    status: "COMPLETED",
    sentAmount: { amount: 5000000, currency: EUR },
    receivedAmount: { amount: 5450000, currency: USD },
    exchangeRate: 1.09,
    fees: 0,
    description: "Client payment from EU",
    destination: "Internal USD Account",
    source: "IBAN: DE89 3704 ****",
    createdAt: "2025-10-16T10:30:00Z",
    settledAt: "2025-10-16T10:32:00Z",
  },
  {
    id: "tx_01HQ4A1B2C3I",
    customerId: "cust_01HQ3X5K7N2M8R",
    platformCustomerId: "user-003",
    type: "OUTGOING",
    status: "FAILED",
    sentAmount: { amount: 2500000, currency: USD },
    receivedAmount: { amount: 0, currency: MXN },
    fees: 0,
    description: "Mexico office funding",
    destination: "CLABE: 0021 8001 ****",
    source: "Internal USD Account",
    createdAt: "2025-10-15T13:00:00Z",
  },
  {
    id: "tx_01HQ4A1B2C3J",
    customerId: "cust_01HQ3X5K7N2M8T",
    platformCustomerId: "user-005",
    type: "OUTGOING",
    status: "COMPLETED",
    sentAmount: { amount: 100000, currency: USD },
    receivedAmount: { amount: 99800, currency: USDC },
    exchangeRate: 0.998,
    fees: 100,
    description: "USDC purchase",
    destination: "Solana: 9xKL...p2Rq",
    source: "Internal USD Account",
    createdAt: "2025-10-14T11:00:00Z",
    settledAt: "2025-10-14T11:01:00Z",
  },
  {
    id: "tx_01HQ4A1B2C3K",
    customerId: "cust_01HQ3X5K7N2M8U",
    platformCustomerId: "user-006",
    type: "OUTGOING",
    status: "COMPLETED",
    sentAmount: { amount: 2500000, currency: EUR },
    receivedAmount: { amount: 2725000, currency: USD },
    exchangeRate: 1.09,
    fees: 500,
    description: "USD treasury allocation",
    destination: "US Account ****3388",
    source: "Internal EUR Account",
    createdAt: "2025-10-13T09:00:00Z",
    settledAt: "2025-10-13T09:03:00Z",
  },
  {
    id: "tx_01HQ4A1B2C3L",
    customerId: "cust_01HQ3X5K7N2M8U",
    platformCustomerId: "user-006",
    type: "INCOMING",
    status: "COMPLETED",
    sentAmount: { amount: 50000000, currency: BTC },
    receivedAmount: { amount: 34000000, currency: EUR },
    exchangeRate: 62800,
    fees: 0,
    description: "BTC liquidation",
    destination: "Internal EUR Account",
    source: "Spark: node@nordic.btc",
    createdAt: "2025-10-10T15:00:00Z",
    settledAt: "2025-10-10T15:05:00Z",
  },
  {
    id: "tx_01HQ4A1B2C3M",
    customerId: "cust_01HQ3X5K7N2M8Y",
    platformCustomerId: "user-010",
    type: "OUTGOING",
    status: "PENDING",
    sentAmount: { amount: 150000, currency: GBP },
    receivedAmount: { amount: 190500, currency: USD },
    exchangeRate: 1.27,
    fees: 300,
    description: "USD wire transfer",
    destination: "US Account ****5566",
    source: "Internal GBP Account",
    createdAt: "2025-10-20T07:00:00Z",
  },
  {
    id: "tx_01HQ4A1B2C3N",
    customerId: "cust_01HQ3X5K7N2M9A",
    platformCustomerId: "user-012",
    type: "OUTGOING",
    status: "COMPLETED",
    sentAmount: { amount: 8000000, currency: USD },
    receivedAmount: { amount: 7960000, currency: USDC },
    exchangeRate: 0.995,
    fees: 1600,
    description: "USDC liquidity pool funding",
    destination: "Polygon: 0xabc...def",
    source: "Internal USD Account",
    createdAt: "2025-10-12T12:00:00Z",
    settledAt: "2025-10-12T12:01:00Z",
  },
  {
    id: "tx_01HQ4A1B2C3O",
    customerId: "cust_01HQ3X5K7N2M9A",
    platformCustomerId: "user-012",
    type: "INCOMING",
    status: "COMPLETED",
    sentAmount: { amount: 3000000, currency: USDC },
    receivedAmount: { amount: 3000000, currency: USD },
    exchangeRate: 1.0,
    fees: 0,
    description: "USDC redemption",
    destination: "Internal USD Account",
    source: "Base: 0x9f8e...1a2b",
    createdAt: "2025-10-11T08:30:00Z",
    settledAt: "2025-10-11T08:31:00Z",
  },
  {
    id: "tx_01HQ4A1B2C3P",
    customerId: "cust_01HQ3X5K7N2M8P",
    platformCustomerId: "user-001",
    type: "OUTGOING",
    status: "REFUNDED",
    sentAmount: { amount: 75000, currency: USD },
    receivedAmount: { amount: 0, currency: GBP },
    fees: 150,
    description: "UK payment (refunded)",
    destination: "GBP Acct: ****3456",
    source: "Internal USD Account",
    createdAt: "2025-10-08T10:00:00Z",
    settledAt: "2025-10-09T14:00:00Z",
  },
  {
    id: "tx_01HQ4A1B2C3Q",
    customerId: "cust_01HQ3X5K7N2M8R",
    platformCustomerId: "user-003",
    type: "OUTGOING",
    status: "COMPLETED",
    sentAmount: { amount: 500000, currency: USDC },
    receivedAmount: { amount: 500000, currency: USD },
    exchangeRate: 1.0,
    fees: 100,
    description: "USDC to USD conversion",
    destination: "US Account ****8812",
    source: "Internal USDC Account",
    createdAt: "2025-10-09T14:00:00Z",
    settledAt: "2025-10-09T14:01:00Z",
  },
];

// -- API Tokens --
export const mockApiTokens: ApiToken[] = [
  {
    id: "tok_01HQ5R1A2B3C",
    name: "Production API Key",
    permissions: ["VIEW", "TRANSACT", "MANAGE"],
    clientId: "eo-api-token-prod-8a1b2c3d",
    createdAt: "2025-06-01T10:00:00Z",
    updatedAt: "2025-06-01T10:00:00Z",
  },
  {
    id: "tok_01HQ5R1A2B3D",
    name: "Read-Only Dashboard",
    permissions: ["VIEW"],
    clientId: "eo-api-token-readonly-4e5f6g7h",
    createdAt: "2025-08-15T14:30:00Z",
    updatedAt: "2025-08-15T14:30:00Z",
  },
  {
    id: "tok_01HQ5R1A2B3E",
    name: "Sandbox Testing",
    permissions: ["VIEW", "TRANSACT"],
    clientId: "eo-api-token-sandbox-9i0j1k2l",
    createdAt: "2025-09-20T09:00:00Z",
    updatedAt: "2025-09-20T09:00:00Z",
  },
];

// -- Dashboard Stats --
export const mockDashboardStats: DashboardStats = {
  totalCustomers: mockCustomers.length,
  kycApproved: mockCustomers.filter(
    (c) => c.kycStatus === "APPROVED" || c.kycStatus === "MANUALLY_APPROVED",
  ).length,
  kycPending: mockCustomers.filter((c) => c.kycStatus === "PENDING_REVIEW")
    .length,
  kycRejected: mockCustomers.filter(
    (c) => c.kycStatus === "REJECTED" || c.kycStatus === "MANUALLY_REJECTED",
  ).length,
  totalTransactions: mockTransactions.length,
  totalVolume: {
    amount: mockTransactions
      .filter((t) => t.status === "COMPLETED")
      .reduce((sum, t) => sum + t.sentAmount.amount, 0),
    currency: USD,
  },
  activeAccounts: mockExternalAccounts.filter((a) => a.status === "ACTIVE")
    .length,
};

// -- Helper functions --

export function formatCurrencyAmount(ca: {
  amount: number;
  currency: { symbol: string; decimals: number; code: string };
}): string {
  const value = ca.amount / Math.pow(10, ca.currency.decimals);
  if (ca.currency.code === "BTC") {
    return `${ca.currency.symbol}${value.toFixed(ca.currency.decimals)}`;
  }
  return `${ca.currency.symbol}${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function getCustomerAccounts(customerId: string) {
  return {
    internal: mockInternalAccounts.filter((a) => a.customerId === customerId),
    external: mockExternalAccounts.filter((a) => a.customerId === customerId),
  };
}

export function getCustomerTransactions(customerId: string) {
  return mockTransactions.filter((t) => t.customerId === customerId);
}

// -- Demo exchange rates --
export const DEMO_EXCHANGE_RATES: Record<string, number> = {
  "USD:EUR": 0.92,
  "USD:GBP": 0.79,
  "USD:BRL": 5.45,
  "USD:MXN": 17.15,
  "USD:BTC": 0.0000148,
  "USD:USDC": 1.0,
  "USD:INR": 83.50,
  "USD:NGN": 1550,
  "EUR:USD": 1.09,
  "EUR:GBP": 0.86,
  "EUR:BTC": 0.0000161,
  "EUR:USDC": 1.09,
  "GBP:USD": 1.27,
  "GBP:EUR": 1.16,
  "BTC:USD": 67500,
  "BTC:EUR": 62100,
  "BTC:USDC": 67500,
  "USDC:USD": 1.0,
  "USDC:EUR": 0.92,
  "USDC:BTC": 0.0000148,
  "BRL:USD": 0.183,
  "MXN:USD": 0.058,
  "INR:USD": 0.012,
  "NGN:USD": 0.000645,
};
